/**
 * IncassoDebtSection
 *
 * Reusable section that shows incasso debt evolution for a contact or company.
 * Fetches snapshot time-series data and renders a line chart.
 * Only renders if data exists — hides itself when no incasso history is found.
 */

import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { useSession } from '@clerk/clerk-react';
import { ChartLineUp } from '@phosphor-icons/react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler,
);

interface SnapshotDataPoint {
  period: string;
  periodLabel: string;
  totalDebt: number;
  activeCases: number;
  avgDaysOverdue: number;
}

interface SnapshotTimeSeries {
  granularity: string;
  data: SnapshotDataPoint[];
}

const formatEuro = (amount: number): string =>
  new Intl.NumberFormat('nl-BE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

interface IncassoDebtSectionProps {
  /** Filter by contact ID (looks up associated incasso tickets) */
  contactId?: number;
  /** Filter by company ID (looks up associated incasso tickets) */
  companyId?: number;
}

export const IncassoDebtSection = ({ contactId, companyId }: IncassoDebtSectionProps) => {
  const { session } = useSession();
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const chartRef = useRef<any>(null);

  const [timeSeries, setTimeSeries] = useState<SnapshotTimeSeries | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTimeSeries = useCallback(async () => {
    if (!sessionRef.current) return;
    if (!contactId && !companyId) return;

    setIsLoading(true);
    try {
      const params = new URLSearchParams({ granularity: 'day' });
      if (contactId) params.set('contactId', String(contactId));
      if (companyId) params.set('companyId', String(companyId));

      const response = await fetchWithAuth(
        `${API_URL}/rental/incasso-tickets/snapshots/time-series?${params}`,
        { method: 'GET' },
        sessionRef.current,
      );
      if (!response.ok) throw new Error('Failed to fetch incasso time series');
      const data: SnapshotTimeSeries = await response.json();
      setTimeSeries(data);
    } catch (error) {
      console.error('[IncassoDebtSection] Error fetching time series:', error);
    } finally {
      setIsLoading(false);
    }
  }, [contactId, companyId]);

  useEffect(() => {
    fetchTimeSeries();
  }, [fetchTimeSeries]);

  const chartData = useMemo(() => {
    if (!timeSeries?.data || timeSeries.data.length === 0) return null;

    return {
      labels: timeSeries.data.map((d) => d.periodLabel),
      datasets: [
        {
          label: 'Openstaand',
          data: timeSeries.data.map((d) => d.totalDebt),
          borderColor: '#EA8162',
          backgroundColor: 'rgba(234, 129, 98, 0.15)',
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 6,
          pointBackgroundColor: '#EA8162',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          borderWidth: 2,
        },
      ],
    };
  }, [timeSeries]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#31403B',
          titleColor: '#F9F0EE',
          bodyColor: '#F9F0EE',
          borderColor: '#EA8162',
          borderWidth: 1,
          padding: 8,
          titleFont: { size: 11 },
          bodyFont: { size: 10 },
          callbacks: {
            label: (context: any) => `Schuld: ${formatEuro(context.parsed.y)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            font: { size: 9 },
            maxRotation: 0,
            maxTicksLimit: 10,
          },
        },
        y: {
          beginAtZero: true,
          grid: { color: 'rgba(49, 64, 59, 0.08)' },
          ticks: {
            font: { size: 9 },
            callback: (value: any) => formatEuro(value),
          },
        },
      },
    }),
    [],
  );

  // Compute summary
  const firstPoint = timeSeries?.data?.[0];
  const latestPoint = timeSeries?.data?.[timeSeries.data.length - 1];
  const debtDelta =
    firstPoint && latestPoint ? latestPoint.totalDebt - firstPoint.totalDebt : null;
  const maxDebt = timeSeries?.data
    ? Math.max(...timeSeries.data.map((d) => d.totalDebt))
    : 0;

  // Don't render anything while loading or if no data
  if (isLoading) return null;
  if (!timeSeries?.data || timeSeries.data.length === 0) return null;

  return (
    <div className="flex flex-col gap-2 rounded-md border border-neutral-primary/15 bg-white p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-neutral-700">
          <ChartLineUp weight="duotone" size={16} className="text-[#EA8162]" />
          Incasso Schuldopbouw
        </div>
        <div className="flex items-center gap-3 text-xs">
          {latestPoint && (
            <span className="text-neutral-secondary-mid">
              Huidig:{' '}
              <span className="font-semibold text-neutral-700">
                {formatEuro(latestPoint.totalDebt)}
              </span>
            </span>
          )}
          <span className="text-neutral-secondary-mid">
            Piek:{' '}
            <span className="font-semibold text-neutral-700">{formatEuro(maxDebt)}</span>
          </span>
          {debtDelta !== null && (
            <span
              className={`font-semibold ${
                debtDelta > 0
                  ? 'text-red-600'
                  : debtDelta < 0
                    ? 'text-green-600'
                    : 'text-neutral-500'
              }`}
            >
              {debtDelta > 0 ? '+' : ''}
              {formatEuro(debtDelta)}
            </span>
          )}
        </div>
      </div>

      {/* Chart */}
      {chartData && (
        <div className="h-[140px] w-full">
          <Line ref={chartRef} data={chartData} options={chartOptions} />
        </div>
      )}
    </div>
  );
};
